import type { CreateClientConfig } from './generated/client.gen';

import { env } from '@/env';

import { signedOutReasonFrom } from './auth-events';
import {
  ApiError,
  type Envelope,
  isInactiveAccount,
  reasonIn,
  REVOKED_SESSION_TEXT,
} from './envelope';
import { organizationScope } from './organization-scope';
import { endSession, REFRESH_ENDPOINT, refreshTokens } from './refresh';
import { tokenStore } from './token-store';

/**
 * Initial configuration for the generated API client. `@hey-api/openapi-ts`
 * calls this once when the client module loads.
 */

/**
 * Endpoints that check a credential must never trigger a token refresh. A 401
 * from one of these means "wrong password" or "wrong code", not "your session
 * expired".
 *
 * These are the addresses the portal actually calls. The list used to name the
 * mobile app's endpoints instead, none of which the portal uses, so every entry
 * was dead. The visible consequence: mistype your reset code while signed in,
 * and the 401 was read as an expired session, a renewal was attempted, it
 * failed, and you were signed out with no explanation.
 *
 * One set of addresses now serves all three roles, so there is one list here
 * rather than one per kind of account.
 */
const PORTAL_AUTH = '/activist-arc/organization/auth';

/** How a request says which organization it is about. */
export const ORGANIZATION_HEADER = 'X-Organization-Id';

const AUTH_FREE_PATHS = new Set([
  `${PORTAL_AUTH}/login`,
  `${PORTAL_AUTH}/send-code`,
  `${PORTAL_AUTH}/verify`,
  `${PORTAL_AUTH}/forgot-password`,
  `${PORTAL_AUTH}/reset-password`,
  // Renewing is itself a credential check. A 401 here is handled where it is
  // called, and refreshing in response to it would be circular.
  REFRESH_ENDPOINT,
]);

/**
 * The same rule for whole families of addresses rather than exact ones.
 *
 * The public invitation routes carry the token in the path, so there is no
 * fixed address to list. They take no credential at all - the link is the
 * credential - so a 410 or a 404 from one of them means the link is finished,
 * never that a session expired. This matters for somebody who is already
 * signed in and opens an invitation link: without it, their own session would
 * be renewed, or ended, because of a dead link belonging to somebody else.
 */
const AUTH_FREE_PREFIXES = ['/activist-arc/organization/invitations/'];

function needsNoCredential(pathname: string): boolean {
  return (
    AUTH_FREE_PATHS.has(pathname) ||
    AUTH_FREE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

function withBearer(request: Request, token: string | undefined): Request {
  if (!token) return request;
  const copy = request.clone();
  copy.headers.set('Authorization', `Bearer ${token}`);
  return copy;
}

/**
 * Which organization a portal request is about.
 *
 * Somebody who belongs to one organization may leave this off and the backend
 * infers it; somebody who belongs to several must send it or be answered 400.
 * Sending it always is simpler than deciding per request, and it is also how
 * Activist staff read an organization they are viewing.
 *
 * A header the caller already set wins, so a request that deliberately names
 * a different organization - staff opening one from the staff area - is not
 * overwritten by whichever one happens to be current.
 */
function withOrganization(request: Request): Request {
  if (request.headers.has(ORGANIZATION_HEADER)) return request;
  const organizationId = organizationScope.get();
  if (!organizationId) return request;
  const copy = request.clone();
  copy.headers.set(ORGANIZATION_HEADER, organizationId);
  return copy;
}

/**
 * Adds the bearer token to every request, and retries once after refreshing when
 * the server says the access token has expired.
 *
 * This is a `fetch` wrapper rather than a response interceptor on purpose. A
 * wrapper still holds the original `Request` object, so retrying is just
 * "send the same request again with a new header" — no rebuilding the URL, body
 * or headers by hand, which is where retry logic usually goes wrong.
 */
const authenticatedFetch: typeof fetch = async (input, init) => {
  const request = new Request(input, init);

  // Remembered, because the retry below has to say which token failed. Reading
  // the store again at that point could pick up a token somebody else has
  // already refreshed into it.
  const usedAccessToken = tokenStore.get()?.accessToken;
  let response = await fetch(withOrganization(withBearer(request, usedAccessToken)));

  if (response.status !== 401 && response.status !== 403) return response;

  const pathname = new URL(request.url, globalThis.location?.origin ?? 'http://localhost')
    .pathname;
  if (needsNoCredential(pathname) || !tokenStore.get()) return response;

  // Read the body from a copy, so the caller still receives an unread response.
  const body = (await response
    .clone()
    .json()
    .catch(() => undefined)) as Envelope<unknown> | undefined;

  // A suspended or closed account is refused on every page. That is not
  // about one action, so the person is signed out and told why (B13, D15),
  // rather than meeting an error wherever they click. Any other 403 is about
  // what they tried to do, and goes back to the caller.
  if (response.status === 403) {
    if (isInactiveAccount(body?.error)) endSession('inactive');
    return response;
  }

  // A revoked session cannot be refreshed. Retrying would fail for ever. The
  // reply says why it ended, and the person is told that (B15).
  if ((body?.error ?? '').includes(REVOKED_SESSION_TEXT)) {
    endSession(signedOutReasonFrom(reasonIn(body?.data)));
    return response;
  }

  try {
    const next = await refreshTokens(usedAccessToken);
    response = await fetch(withOrganization(withBearer(request, next.accessToken)));
  } catch (error) {
    // Only a refusal means the session is over. A dropped connection or a
    // server fault says nothing about whether the tokens are still good, and
    // signing somebody out because their Wi-Fi blinked loses whatever they were
    // in the middle of. Anything else is reported as an ordinary failed
    // request, which the caller can retry.
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      // A renewal refused because the session ended, or because the account
      // is no longer active, says so, as above (B13, B15, D25).
      if (isInactiveAccount(error.apiError)) endSession('inactive');
      else
        endSession(
          error.isRevokedSession ? signedOutReasonFrom(error.reason) : 'expired',
        );
    } else {
      throw error;
    }
  }

  return response;
};

export const createClientConfig: CreateClientConfig = (config) => ({
  ...config,
  /**
   * The origin only. The generated paths already start with `/activist-arc`, so
   * repeating it here would produce `/activist-arc/activist-arc/...`.
   *
   * Empty in development, which means "this same origin" and so goes through the
   * Vite dev proxy defined in vite.config.ts.
   */
  baseUrl: env.apiBaseUrl,
  fetch: authenticatedFetch,
});
